import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { graphqlClient } from "@/lib/graphql-client";
import { useAuth } from "./AuthContext";
import {
  OnboardingProgress,
  OnboardingStep,
  StepCompletionResult,
  CompanyStepData,
  StrategicContextData,
  ContextStepData,
  TechnologyStepData,
  AIReadinessData,
  TeamStepData,
  FirstValueChoice,
  StepPayload,
} from "@/pages/onboarding/types";
import {
  SHOULD_SHOW_ONBOARDING,
  MY_ONBOARDING_PROGRESS,
  START_ONBOARDING,
  COMPLETE_WELCOME_STEP,
  COMPLETE_COMPANY_STEP,
  COMPLETE_STRATEGIC_CONTEXT_STEP,
  COMPLETE_CONTEXT_STEP,
  COMPLETE_TECHNOLOGY_STEP,
  COMPLETE_AI_READINESS_STEP,
  COMPLETE_PROVIDER_STEP,
  COMPLETE_TEAM_STEP,
  COMPLETE_FIRST_VALUE_STEP,
  COMPLETE_COMPLETE_STEP,
  SKIP_ONBOARDING_STEP,
  DISMISS_ONBOARDING,
} from "@/graphql/onboarding";

interface OnboardingContextType {
  progress: OnboardingProgress | null;
  loading: boolean;
  error: Error | null;
  showOnboarding: boolean;
  currentStepData: OnboardingStep | null;

  // Actions
  startOnboarding: () => Promise<void>;
  completeStep: (stepKey: string, data?: StepPayload) => Promise<void>;
  skipStep: (stepKey: string) => Promise<void>;
  dismissOnboarding: () => Promise<void>;
  goToStep: (stepKey: string) => void;
  refreshProgress: () => Promise<void>;
}

export const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined
);

interface OnboardingProviderProps {
  children: ReactNode;
}

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
}) => {
  const { user, loading: authLoading, refreshUserProfile } = useAuth();
  const [progress, setProgress] = useState<OnboardingProgress | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Fetch whether to show onboarding
  const fetchShouldShowOnboarding = useCallback(async () => {
    if (!user || authLoading) {
      setShowOnboarding(false);
      return;
    }

    try {
      const result = await graphqlClient.request<{
        shouldShowOnboarding: boolean;
      }>(SHOULD_SHOW_ONBOARDING);

      setShowOnboarding(result.shouldShowOnboarding);
    } catch (err) {
      console.error("Error checking onboarding status:", err);
      setShowOnboarding(false);
    }
  }, [user, authLoading]);

  // Fetch current progress
  const fetchProgress = useCallback(async () => {
    if (!user || authLoading) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await graphqlClient.request<{
        myOnboardingProgress: OnboardingProgress | null;
      }>(MY_ONBOARDING_PROGRESS);

      setProgress(result.myOnboardingProgress);
      setError(null);
    } catch (err) {
      console.error("Error fetching onboarding progress:", err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  // Initialize on mount
  useEffect(() => {
    if (!authLoading && user) {
      fetchShouldShowOnboarding();
      fetchProgress();
    } else if (!authLoading && !user) {
      setLoading(false);
      setShowOnboarding(false);
      setProgress(null);
    }
  }, [user, authLoading, fetchShouldShowOnboarding, fetchProgress]);

  // Start onboarding
  const startOnboarding = useCallback(async () => {
    try {
      const result = await graphqlClient.request<{
        startOnboarding: OnboardingProgress;
      }>(START_ONBOARDING);

      setProgress(result.startOnboarding);
      setShowOnboarding(true);
    } catch (err) {
      console.error("Error starting onboarding:", err);
      throw err;
    }
  }, []);

  // Complete a step
  const completeStep = useCallback(
    async (stepKey: string, data?: StepPayload) => {
      try {
        let result: StepCompletionResult;

        switch (stepKey) {
          case "welcome":
            const welcomeResult = await graphqlClient.request<{
              completeWelcomeStep: StepCompletionResult;
            }>(COMPLETE_WELCOME_STEP);
            result = welcomeResult.completeWelcomeStep;
            break;

          case "company":
            const companyResult = await graphqlClient.request<{
              completeCompanyStep: StepCompletionResult;
            }>(COMPLETE_COMPANY_STEP, { input: data as CompanyStepData });
            result = companyResult.completeCompanyStep;
            break;

          case "strategic":
            const strategicResult = await graphqlClient.request<{
              completeStrategicContextStep: StepCompletionResult;
            }>(COMPLETE_STRATEGIC_CONTEXT_STEP, {
              input: data as StrategicContextData,
            });
            result = strategicResult.completeStrategicContextStep;
            break;

          case "context":
            const contextResult = await graphqlClient.request<{
              completeContextStep: StepCompletionResult;
            }>(COMPLETE_CONTEXT_STEP, { input: data as ContextStepData });
            result = contextResult.completeContextStep;
            break;

          case "technology":
            const techResult = await graphqlClient.request<{
              completeTechnologyStep: StepCompletionResult;
            }>(COMPLETE_TECHNOLOGY_STEP, { input: data as TechnologyStepData });
            result = techResult.completeTechnologyStep;
            break;

          case "ai-readiness":
            const aiResult = await graphqlClient.request<{
              completeAIReadinessStep: StepCompletionResult;
            }>(COMPLETE_AI_READINESS_STEP, { input: data as AIReadinessData });
            result = aiResult.completeAIReadinessStep;
            break;

          case "providers":
            const providerResult = await graphqlClient.request<{
              completeProvidersStep: StepCompletionResult;
            }>(COMPLETE_PROVIDER_STEP);
            result = providerResult.completeProvidersStep;
            break;

          case "team":
            const teamResult = await graphqlClient.request<{
              completeTeamStep: StepCompletionResult;
            }>(COMPLETE_TEAM_STEP, { input: data as TeamStepData });
            result = teamResult.completeTeamStep;
            break;

          case "first-value":
            // data is { choice: "explore" | "organization" | "ignite" }
            const firstValueData = data as { choice: FirstValueChoice };
            const valueResult = await graphqlClient.request<{
              completeFirstValueStep: StepCompletionResult;
            }>(COMPLETE_FIRST_VALUE_STEP, { choice: firstValueData.choice });
            result = valueResult.completeFirstValueStep;
            break;

          case "complete":
            const completeResult = await graphqlClient.request<{
              completeCompleteStep: StepCompletionResult;
            }>(COMPLETE_COMPLETE_STEP);
            result = completeResult.completeCompleteStep;
            break;

          default:
            throw new Error(`Unknown step: ${stepKey}`);
        }

        if (result.progress) {
          setProgress(result.progress);
        }

        // If onboarding is complete, hide it and refresh auth context
        if (result.progress?.status === "COMPLETED") {
          setShowOnboarding(false);
          // Refresh AuthContext so shouldShowOnboarding is updated
          await refreshUserProfile();
        }
      } catch (err) {
        console.error(`Error completing step ${stepKey}:`, err);
        throw err;
      }
    },
    [refreshUserProfile]
  );

  // Skip a step
  const skipStep = useCallback(async (stepKey: string) => {
    try {
      const result = await graphqlClient.request<{
        skipOnboardingStep: StepCompletionResult;
      }>(SKIP_ONBOARDING_STEP, { stepKey });

      if (result.skipOnboardingStep.progress) {
        setProgress(result.skipOnboardingStep.progress);
      }
    } catch (err) {
      console.error(`Error skipping step ${stepKey}:`, err);
      throw err;
    }
  }, []);

  // Dismiss onboarding
  const dismissOnboarding = useCallback(async () => {
    try {
      await graphqlClient.request<{
        dismissOnboarding: boolean;
      }>(DISMISS_ONBOARDING);

      setShowOnboarding(false);
      setProgress(null);
      // Refresh AuthContext so shouldShowOnboarding is updated
      await refreshUserProfile();
    } catch (err) {
      console.error("Error dismissing onboarding:", err);
      throw err;
    }
  }, [refreshUserProfile]);

  // Go to a specific step
  const goToStep = useCallback((stepKey: string) => {
    setProgress((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        currentStep: stepKey,
      };
    });
  }, []);

  // Refresh progress
  const refreshProgress = useCallback(async () => {
    await fetchProgress();
  }, [fetchProgress]);

  // Get current step data
  const currentStepData = progress?.steps.find(
    (step) => step.stepKey === progress.currentStep
  ) || null;

  const value: OnboardingContextType = {
    progress,
    loading,
    error,
    showOnboarding,
    currentStepData,
    startOnboarding,
    completeStep,
    skipStep,
    dismissOnboarding,
    goToStep,
    refreshProgress,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

/**
 * Hook to access onboarding context
 */
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
